// LLM generated - Model Claude Opus 4.5
import get from 'lodash.get';
import type { FormattedErrors } from '../../../../context/utils/formatZodErrors';

/**
 * Recursively searches for the first user-facing error message in a nested error object.
 * Skips internal loading-state messages like "Wait for a field to be loaded".
 *
 * @param errors - The error object to search (can be nested objects or arrays)
 * @returns The first user-facing error message found, or undefined if none
 */
function findFirstError(errors: unknown): string | undefined {
  // Check if it's an array of error messages (leaf node)
  if (Array.isArray(errors) && errors.length > 0 && typeof errors[0] === 'string') {
    const message = errors[0];
    // Skip internal loading-state messages
    if (message.startsWith('Wait for')) return undefined;
    return message;
  }

  // Recursively search nested objects
  if (errors && typeof errors === 'object') {
    for (const value of Object.values(errors)) {
      const found = findFirstError(value);
      if (found) return found;
    }
  }

  return undefined;
}

/**
 * Gets the first child error message for a container field (like ModalFieldGroup).
 *
 * This is used to display the first validation error from child fields
 * when the container itself doesn't have a direct error.
 *
 * @param allErrors - All form errors (FormattedErrors structure)
 * @param sectionKey - The section key this field belongs to
 * @param path - The path array to this field
 * @returns The first child error message, or undefined if no child errors
 */
export function getFirstChildError(
  allErrors: FormattedErrors | null | undefined,
  sectionKey: string | undefined,
  path: string[]
): string | undefined {
  if (!allErrors || !sectionKey) return undefined;

  const groupErrors = get(allErrors, [sectionKey, ...path]);
  if (!groupErrors || typeof groupErrors !== 'object') return undefined;

  return findFirstError(groupErrors);
}
